import { useState } from "react";
import { motion } from "framer-motion";
import { Radar, RefreshCw, Layers, EyeOff, ArrowUpRight } from "lucide-react";
import { SignalModal } from "@/components/triarch/SignalModal";

const CARDS = [
  {
    title: "Signal Mapping",
    line: "Vectors align without announcement.",
    icon: Radar,
    idx: "01",
    fileLabel: "SIGNAL FILE 01",
    paragraphs: [
      "Process of tracing fragmented data points across invisible systems, identifying their origin, movement, correlation, and consequence.",
      "It converts scattered digital noise into structured intelligence by detecting patterns, linking entities, compressing irregular signals, and revealing hidden relationships before they become visible to conventional systems.",
    ],
    definition: "The extraction of structure from invisible digital movement.",
    commands: [
      "Noise becomes trace.",
      "Trace becomes pattern.",
      "Pattern becomes intelligence.",
    ],
    outro: [
      "In Triarch, every signal is not treated as information. It is treated as movement, intent, structure, and risk.",
    ],
  },
  {
    title: "Closed Loop Systems",
    line: "Feedback sealed. Drift contained.",
    icon: RefreshCw,
    idx: "02",
    fileLabel: "RECURSIVE FILE 02",
    paragraphs: [
      "An intelligence structure that does not stop at detection.",
      "It captures signals, processes anomalies, generates intelligence, feeds outcomes back into the system, and continuously refines its own understanding.",
      "Each cycle sharpens the next.",
    ],
    definition:
      "A recursive intelligence cycle where every output strengthens the next signal.",
    commands: ["Signals enter.", "Patterns emerge.", "Intelligence returns."],
    outro: [
      "In Triarch, intelligence is not produced and left behind. Every prompt becomes a signal, every search becomes residue, and every interaction is absorbed into a hidden cycle where observation sharpens correction, correction deepens learning, and learning escalates the system beyond its previous form.",
    ],
  },
  {
    title: "Synthetic Infrastructure",
    line: "Built beneath the visible layer.",
    icon: Layers,
    idx: "03",
    fileLabel: "INFRASTRUCTURE FILE 03",
    paragraphs: [
      "Engineered intelligence layer built from data systems, automated workflows, adaptive models, private repositories, and recursive feedback loops.",
      "It functions as an invisible operational grid where signals are collected, structured, simulated, tested, and redeployed into intelligence.",
    ],
    definition:
      "A hidden operational grid where data, models, repositories, and automated systems converge.",
    commands: ["Not steel.", "Not servers.", "Not visible machinery."],
    outro: [
      "Triarch builds synthetic infrastructure to collect fragmented signals, structure intelligence, simulate outcomes, and strengthen the system beyond physical limits.",
    ],
  },
  {
    title: "Zero Surface Exposure",
    line: "Presence without footprint.",
    icon: EyeOff,
    idx: "04",
    fileLabel: "EXPOSURE FILE 04",
    paragraphs: [
      "Principle that the core system remains unseen, unreachable, and unexposed at the visible layer.",
      "Only controlled outputs are revealed.",
      "The architecture, logic, repositories, intelligence pathways, and internal decision structures remain hidden beneath the interface, reducing attack surface, limiting external visibility, and protecting the system from direct observation.",
    ],
    definition: null,
    commands: [
      "The visible layer reveals nothing essential.",
      "Nothing critical reaches the surface.",
      "The interface is only a shadow.",
    ],
    outro: [
      "The structure remains buried, sealed, and unreadable, allowing intelligence to move without exposing the machinery that forms it.",
      "Triarch exposes only controlled outputs while the deeper architecture, intelligence routes, and internal mechanisms remain sealed beneath the surface.",
    ],
  },
];

export const SignalCards = () => {
  const [active, setActive] = useState(null);

  return (
    <section
      id="signal"
      data-testid="signal-section"
      className="relative scroll-mt-24 py-24 sm:py-28"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="mb-3 flex items-center gap-3">
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.28em] text-white/35">
            // 01 — Signal
          </span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-90px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl font-display text-2xl font-medium tracking-[-0.01em] text-white/90 sm:text-3xl"
        >
          Capabilities are implied, never declared.
        </motion.h2>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CARDS.map((c, i) => (
            <motion.button
              key={c.title}
              type="button"
              data-testid="signal-card"
              onClick={() => setActive(c)}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.6,
                delay: i * 0.06,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group relative flex flex-col overflow-hidden rounded-[var(--radius)] border border-white/10 bg-white/[0.035] p-6 text-left backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:border-[#C9AE78]/25 hover:bg-white/[0.05] hover:shadow-[0_0_42px_rgba(203,174,120,0.12),0_24px_70px_rgba(0,0,0,0.55)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9AE78]/40"
            >
              <div
                className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{
                  background:
                    "radial-gradient(620px 200px at 20% 0%, rgba(203,174,120,0.1), transparent 55%)",
                }}
              />
              <div className="relative flex items-center justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-white/70 transition-colors duration-500 group-hover:border-[#C9AE78]/30 group-hover:text-white">
                  <c.icon className="h-5 w-5" strokeWidth={1.4} />
                </div>
                <span className="font-mono-accent text-[10px] tracking-[0.25em] text-white/25">
                  {c.idx}
                </span>
              </div>
              <h3
                data-testid="signal-card-title"
                className="relative mt-7 font-display text-lg font-medium leading-snug text-white transition-colors duration-300 group-hover:text-[#C9AE78]"
              >
                {c.title}
              </h3>
              <p
                data-testid="signal-card-line"
                className="relative mt-2 text-sm text-white/45"
              >
                {c.line}
              </p>
              <div className="relative mt-6 h-px w-full bg-gradient-to-r from-white/10 to-transparent" />
              <div className="relative mt-4 flex items-center justify-between">
                <span className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-white/25">
                  Classified
                </span>
                <span className="flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-[0.18em] text-[#C9AE78]/0 transition-colors duration-300 group-hover:text-[#C9AE78]/80">
                  Open File
                  <ArrowUpRight className="h-3 w-3" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <SignalModal card={active} onClose={() => setActive(null)} />
    </section>
  );
};
